import { Icon, IconButton } from "@material-ui/core";
import {
  OrientationType,
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import ScaleAnimation from "components/scaleAnimation/ScaleAnimation";
import { Product } from "domains/catalog/aggregates/Product";
import { SeccionedProduct } from "domains/catalog/aggregates/SeccionedProduct";
import { Store } from "domains/catalog/aggregates/Store";
import { CefSharpWeighingMachine } from "Infrastructure/services/cefSharpService/local/CefSharpWeighingMachine";
import { ICartContext } from "modules/cart/domain/interfaces/IUseCaseCart";
import { useCartStore } from "modules/cart/infra/store/CartStore";
import { ReactComponent as Attention } from "../../../../assets/icons/attention.svg";
import Carousel from "modules/cart/presentation/cartView/carousel/Carousel";
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { moneyMask, roundValue } from "utils/utils";
import styles from "./ProductKilo.module.scss";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import { isReactNative } from "utils/constants";

const WEIGHT_EXPIRATION_TIMER = 30;

let expirationWeightTimeout: NodeJS.Timeout;

const cefSharpWeighingMachine = CefSharpWeighingMachine();

interface ProductKiloProps {
  product: Product;
  onClose: () => void;
  store: Store | undefined;
}

export const ProductKilo: FC<ProductKiloProps> = ({
  product,
  onClose,
  store,
}) => {
  const { resolution, orientation } = useScreenStore();
  const { addLog } = useAppInsights();
  const [addProductCart] = useCartStore((state: ICartContext) => [
    state.addProductCart,
  ]);

  const firstLoad = useRef(true);

  const [weight, setWeight] = useState(0);
  const [expirationCounter, setExpirationCounter] = useState(
    WEIGHT_EXPIRATION_TIMER
  );

  const suggestionProducts = useMemo(() => {
    let sugProducts: Product[] = [];
    if (store) {
      store.seccionedProduct.forEach((it: SeccionedProduct) => {
        it.products.forEach((p: Product) => {
          if (product?.cartRecommendationByProduct?.includes(p.id)) {
            sugProducts.push(p);
          }
        });
      });
    }
    return sugProducts;
  }, [product, store]);

  const onWeighing = useCallback((_weight: number) => {
    setWeight(_weight);
  }, []);

  const initconection = useCallback(async () => {
    const PORTA = localStorage.getItem("@weighning-port");
    try {
      if (isReactNative) {
        // startWeighingScale é idempotente: se balança já está ativa em background
        // apenas registra os listeners do modal (setWeight + onWeightUpdate)
        await window.RNBridge.callNative("startWeighingScale");
      } else {
        await cefSharpWeighingMachine.getValueFromWighingScale(PORTA ?? "COM0");
      }
    } catch (error) {
      addLog("Weighing", error);
    }
  }, [addLog]);

  const stopWeighingMachine = useCallback(() => {
    clearTimeout(expirationWeightTimeout);
    window.setWeight = () => {};
    if (isReactNative) {
      window.RNBridge.callNative("stopWeighingScale").catch(() => {});
      // Reinicia background: reseta a borda de subida para que o modal possa
      // reabrir automaticamente mesmo sem tirar o produto da balança
      window.RNBridge.callNative("startBackgroundScale").catch(() => {});
    }
  }, []);

  const closeModal = useCallback(() => {
    stopWeighingMachine();
    onClose();
  }, [onClose, stopWeighingMachine]);

  useEffect(() => {
    if (expirationCounter > 0 && !weight) {
      expirationWeightTimeout = setTimeout(() => {
        setExpirationCounter((prev) => prev - 1);
      }, 1000);
    } else if (expirationCounter === 0 && !weight) {
      closeModal();
    }
    return () => {
      clearTimeout(expirationWeightTimeout);
    };
  }, [
    closeModal,
    expirationCounter,
    initconection,
    onClose,
    stopWeighingMachine,
    weight,
  ]);

  const initWeighingMachine = useCallback(() => {
    setWeight(0);
    initconection();
    setExpirationCounter(WEIGHT_EXPIRATION_TIMER);
    window.setWeight = onWeighing;
    return () => {
      stopWeighingMachine();
    };
  }, [stopWeighingMachine, initconection, onWeighing]);

  useEffect(() => {
    if (firstLoad.current) {
      initWeighingMachine();
      firstLoad.current = false;
    }
  }, [initWeighingMachine]);

  const onAddProduct = useCallback(() => {
    const finalWeight = Number(
      ((weight + (product?.tare ? -product.tare : 0)) / 1000).toFixed(3)
    );
    const price = roundValue(product.price * finalWeight);
    addProductCart(
      { ...product, isWeight: true, price: price, totalPrice: price },
      finalWeight,
      []
    );
    closeModal();
  }, [addProductCart, closeModal, product, weight]);

  const onImgError = (ev: React.ChangeEvent<HTMLImageElement>) => {
    ev.target.src = require("../../../../assets/images/no-image.png");
  };

  const validWeight = useMemo(
    () => weight > (product?.tare ?? 0),
    [product, weight]
  );

  return (
    <div id={styles.ProductKilo}>
      <IconButton className={styles.close} onClick={closeModal}>
        <Icon>close</Icon>
      </IconButton>

      <div
        className={styles.container}
        style={{
          padding: resolution === ResolutionType.FULL_HD ? 80 : 30,
        }}
      >
        <div className={styles.description}>
          <img
            src={product.thumbnailUrl}
            onErrorCapture={onImgError}
            alt="Produto"
            style={{
              width: resolution === ResolutionType.FULL_HD ? 250 : 200,
              height: resolution === ResolutionType.FULL_HD ? 200 : 150,
            }}
          />
          <div
            style={{
              marginLeft: resolution === ResolutionType.FULL_HD ? 32 : 24,
            }}
          >
            <p
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 36 : 26,
                maxWidth: resolution === ResolutionType.FULL_HD ? 600 : 400,
              }}
            >
              {product.name}
            </p>
            <p
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 26 : 18,
                maxHeight: resolution === ResolutionType.FULL_HD ? 200 : 140,
                maxWidth: resolution === ResolutionType.FULL_HD ? 600 : 400,
              }}
            >
              {product?.description}
            </p>
          </div>
        </div>

        <div
          className={styles.weighingContainer}
          style={{
            marginTop:
              orientation === OrientationType.PORTRAIT &&
              resolution === ResolutionType.FULL_HD
                ? 60
                : 0,
          }}
        >
          {weight ? (
            <>
              <div
                className={
                  resolution === ResolutionType.FULL_HD
                    ? styles.orderValues
                    : styles.orderValuesHD
                }
                style={{
                  width:
                    orientation === OrientationType.PORTRAIT ? "100%" : "25%",
                  alignSelf:
                    orientation === OrientationType.LANDSCAPE
                      ? "flex-start"
                      : undefined,
                }}
              >
                <div>
                  <span>Preço:</span>
                  <span>{moneyMask(product.price)}/Kg</span>
                </div>
                <div>
                  <span>Peso:</span>
                  <span>
                    {(weight / 1000).toFixed(3).replace(".", ",")}
                    Kg
                  </span>
                </div>
                <div>
                  <span>Tara:</span>
                  <span>
                    {((product?.tare ?? 0) / 1000).toFixed(3).replace(".", ",")}
                    Kg
                  </span>
                </div>
                <div>
                  <span>Peso s/ tara:</span>
                  <span>
                    {((weight - (product?.tare ?? 0)) / 1000)
                      .toFixed(3)
                      .replace(".", ",")}
                    Kg
                  </span>
                </div>
                <div>
                  <span>
                    <b>Total:</b>
                  </span>
                  <span>
                    <b>
                      {validWeight
                        ? moneyMask(
                            (product.price *
                              (weight - (product.tare ? product.tare : 0))) /
                              1000
                          )
                        : moneyMask(0)}
                    </b>
                  </span>
                </div>
              </div>
              <div>
                {validWeight ? (
                  <div className={styles.footer}>
                    {suggestionProducts.length > 0 && (
                      <div
                        style={{
                          width:
                            orientation === OrientationType.LANDSCAPE
                              ? "70%"
                              : undefined,
                          alignSelf:
                            orientation === OrientationType.LANDSCAPE
                              ? "flex-end"
                              : undefined,
                        }}
                      >
                        <span>
                          Peça <b>também</b>
                        </span>
                        <Carousel products={suggestionProducts} />
                      </div>
                    )}
                    <div className={styles.continue} onClick={onAddProduct}>
                      <span>
                        {moneyMask(
                          (product.price *
                            (weight - (product.tare ? product.tare : 0))) /
                            1000
                        )}
                      </span>
                      <span>Continuar</span>
                    </div>
                  </div>
                ) : (
                  <div
                    className={styles.warning}
                    style={{
                      left:
                        orientation === OrientationType.LANDSCAPE ? "40%" : 0,
                    }}
                  >
                    <Attention />
                    <h3>Produto não atingiu o peso mínimo</h3>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={styles.weighing}>
              <p>Coloque o produto sobre a balança</p>
              <ScaleAnimation
                color="var(--primary)"
                size={
                  orientation === OrientationType.PORTRAIT
                    ? resolution === ResolutionType.FULL_HD
                      ? 500
                      : 350
                    : 200
                }
                active={true}
                loop={true}
                dropDurationMs={2600}
                spinDurationMs={1200}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
